/**
 * Flow Optimizer — Queue & Rotation API client
 * For Media Devoted | Created By Ciro Autiero
 *
 * Bank-backed queue + rotation endpoints under `/{provider}/flow-optimizer/*`.
 * Mirrors flow-optimizer-api.ts: unwraps `data.data ?? data`, defaults to
 * 'voluum' for back-compat. Voluum/Binom only: every call takes a
 * QueueProvider (from queueProviderOf), and a tracker without a queue is
 * refused before any request is sent.
 */

import api from '@/services/api';
import {
  queueProviderOf,
  type QueueProvider,
} from '@/features/trackers/flow-optimizer/queue-provider';
import type {
  ElementEventsResult,
  FlowLayer,
  PromoteElementResult,
  PromotePlanResult,
  QueueListResult,
  RequeueElementResult,
  RotationHistoryResult,
  RotationPreviewResult,
  SimulatedStats,
  SimulatedStatsInput,
  DemotePlanResult,
  DemoteElementResult,
  DemoteDestination,
} from '@trafficpulse/shared';

function baseFor(provider: QueueProvider = 'voluum'): string {
  if (queueProviderOf(provider) !== provider) throw new Error(`No queue on ${provider}`);
  return `/${provider}/flow-optimizer`;
}

export async function getQueue(
  campaignId: string,
  provider: QueueProvider = 'voluum',
): Promise<QueueListResult> {
  const { data } = await api.get(`${baseFor(provider)}/${campaignId}/queue`);
  return (data.data ?? data) as QueueListResult;
}

export async function previewRotation(
  campaignId: string,
  provider: QueueProvider = 'voluum',
): Promise<RotationPreviewResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/preview`, {});
  return (data.data ?? data) as RotationPreviewResult;
}

export async function applyRotation(
  campaignId: string,
  provider: QueueProvider = 'voluum',
): Promise<RotationPreviewResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/apply`, {});
  return (data.data ?? data) as RotationPreviewResult;
}

/** Bring an eliminated element back into the queue (no tracker write). */
export async function requeueElement(
  campaignId: string,
  body: { layer: FlowLayer; bankId: string; to?: 'queue' | 'waiting' },
  provider: QueueProvider = 'voluum',
): Promise<RequeueElementResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/requeue`, body);
  return (data.data ?? data) as RequeueElementResult;
}

/** What "Move to live now" WOULD do — read-only, never writes. */
export async function planPromote(
  campaignId: string,
  body: { layer: FlowLayer; bankId: string },
  provider: QueueProvider = 'voluum',
): Promise<PromotePlanResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/promote/plan`, body);
  return (data.data ?? data) as PromotePlanResult;
}

/** Promote one queued/waiting element to live NOW (writes to the tracker). */
export async function promoteToLive(
  campaignId: string,
  body: {
    layer: FlowLayer;
    bankId: string;
    victimBankId?: string;
    /** False = refuse rather than evict a live element. Bulk promotes set it. */
    allowReplace?: boolean;
  },
  provider: QueueProvider = 'voluum',
): Promise<PromoteElementResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/promote`, body);
  return (data.data ?? data) as PromoteElementResult;
}

/** One element's lifecycle timeline (newest first). */
export async function getQueueEvents(
  campaignId: string,
  params: { layer: FlowLayer; bankId: string },
  provider: QueueProvider = 'voluum',
): Promise<ElementEventsResult> {
  const { data } = await api.get(`${baseFor(provider)}/${campaignId}/queue/events`, { params });
  return (data.data ?? data) as ElementEventsResult;
}

export async function getRotationHistory(
  campaignId: string,
  provider: QueueProvider = 'voluum',
  limit = 20,
): Promise<RotationHistoryResult> {
  const { data } = await api.get(
    `${baseFor(provider)}/${campaignId}/rotation/history`,
    { params: { limit } },
  );
  return (data.data ?? data) as RotationHistoryResult;
}

export async function getSimulatedStats(
  campaignId: string,
  provider: QueueProvider = 'voluum',
): Promise<SimulatedStats[]> {
  const { data } = await api.get(
    `${baseFor(provider)}/${campaignId}/simulated-stats`,
  );
  return (data.data ?? data) as SimulatedStats[];
}

export async function putSimulatedStats(
  campaignId: string,
  body: SimulatedStatsInput,
  provider: QueueProvider = 'voluum',
): Promise<SimulatedStats> {
  const { data } = await api.put(
    `${baseFor(provider)}/${campaignId}/simulated-stats`,
    body,
  );
  return (data.data ?? data) as SimulatedStats;
}

export async function deleteSimulatedStats(
  campaignId: string,
  id: string,
  provider: QueueProvider = 'voluum',
): Promise<void> {
  await api.delete(`${baseFor(provider)}/${campaignId}/simulated-stats/${id}`);
}

/** Read-only: can this LIVE element be moved off the tracker, and if not, why. */
export async function planDemote(
  campaignId: string,
  body: { layer: FlowLayer; bankId: string },
  provider: QueueProvider = 'voluum',
): Promise<DemotePlanResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/demote/plan`, body);
  return (data.data ?? data) as DemotePlanResult;
}

/** Move a LIVE element off the tracker to the queue, Waiting, or eliminated.
 *  The freed slot is left for the next rotation — this never backfills, with
 *  ONE exception: `coverFloor` authorises the swap a `needs-cover` plan named,
 *  promoting that queued element in the same action to hold the live floor. */
export async function demoteElement(
  campaignId: string,
  body: {
    layer: FlowLayer;
    bankId: string;
    to: DemoteDestination;
    coverFloor?: boolean;
  },
  provider: QueueProvider = 'voluum',
): Promise<DemoteElementResult> {
  const { data } = await api.post(`${baseFor(provider)}/${campaignId}/rotation/demote`, body);
  return (data.data ?? data) as DemoteElementResult;
}
